import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  StatesButton,
  SpinnerTypes,
  TextField,
  Spinner,
  ThreeDotsLoader,
  StatesButtonStates,
} from 'wix-ui-tpa/cssVars';
import { useApplicationContext } from '../../core/hooks/useApplicationContext/useApplicationContext';
import { classes } from './AmountInput.st.css';
import { ApplicationStatus, BIClickEvents } from '../../../../types/types';
import { useLocaleKeys } from '../../../../locale-keys/LocaleKeys';
import {
  ErrorSection,
  ErrorSectionDataHooks,
} from '../ErrorSection/ErrorSection';
import { useClickBiEvent } from '../../core/hooks/useClickBiEvent';
import { useErrorSectionText } from '../../core/hooks/useErrorSectionText';
import { useExperiments } from '@wix/yoshi-flow-editor';
import { SPECS } from '../../../../consts';
import { underline } from '@wix/wix-base-ui/src/controls/textFormatting/textFormatting';

export enum AmountInputDataHooks {
  Container = 'amount-input-container',
  TextInput = 'amount-input-text-input',
  ApplyButton = 'amount-input-button',
  TextLoader = 'amount-input-text-loader',
  ThreeDotsLoaderIcon = 'amount-input-three-dots-loader',
}

export interface AmountInputProps {}

export const AmountInput: FC<AmountInputProps> = () => {
  const {
    formattedRedeemableStoreCredit,
    redeemableStoreCredit,
    updateCheckout,
    status,
    giftCardError,
    currencySymbol,
    maxRedeemableAmount,
  } = useApplicationContext();
  const localeKeys = useLocaleKeys();
  const { reportClickBiEvent } = useClickBiEvent();
  const statesButtonRef = useRef<StatesButton>(null);
  const { getErrorSectionText } = useErrorSectionText();
  const { experiments } = useExperiments();
  const [customRedeemAmount, setCustomRedeemAmount] = useState(
    redeemableStoreCredit?.toString(),
  );
  const customRedeemAmountIsEnabled = experiments.enabled(
    SPECS.RiseRedeemCustomAmount,
  );

  const validateAndUpdateAmount = (inputValue: string) => {
    if (inputValue === '' || inputValue === '.') {
      setCustomRedeemAmount('');
      return;
    }
    const numberRegex = /^\d*\.?\d{0,2}$/;
    if (!numberRegex.test(inputValue)) {
      return;
    }

    if (
      maxRedeemableAmount &&
      parseFloat(inputValue) > parseFloat(maxRedeemableAmount)
    ) {
      setCustomRedeemAmount(
        parseFloat(maxRedeemableAmount).toFixed(2).toString(),
      );
      return;
    }

    setCustomRedeemAmount(inputValue);
  };
  const isButtonDisabled = customRedeemAmountIsEnabled
    ? status === ApplicationStatus.UPDATING ||
      status === ApplicationStatus.Disabled ||
      parseFloat(customRedeemAmount ?? '0') === 0 ||
      customRedeemAmount?.length === 0
    : status === ApplicationStatus.UPDATING ||
      status === ApplicationStatus.Disabled ||
      status === ApplicationStatus.REDEEMED;

  useEffect(() => {
    if (giftCardError) {
      statesButtonRef.current?.focus();
    }
  }, [giftCardError, statesButtonRef]);

  const statesButtonIdleLabel = () => {
    const defaultCtaText = localeKeys.checkoutSlot.redeemStoreCredit.cta();

    if (!customRedeemAmountIsEnabled) {
      return defaultCtaText;
    }

    if (status === ApplicationStatus.IDLE) {
      return defaultCtaText;
    }

    if (status === ApplicationStatus.REDEEMED) {
      return 'Update';
    }

    return defaultCtaText;
  };

  return (
    <div>
      <div
        className={classes.container}
        data-hook={AmountInputDataHooks.Container}
      >
        <TextField
          className={
            customRedeemAmountIsEnabled
              ? classes.editableTextField
              : classes.textField
          }
          data-hook={AmountInputDataHooks.TextInput}
          id={AmountInputDataHooks.TextInput}
          prefix={
            customRedeemAmountIsEnabled && currencySymbol
              ? currencySymbol
              : undefined
          }
          value={
            customRedeemAmountIsEnabled
              ? customRedeemAmount
              : formattedRedeemableStoreCredit
          }
          successIcon
          disabled={
            customRedeemAmountIsEnabled ? !customRedeemAmountIsEnabled : true
          }
          newErrorMessage
          suffix={
            status === ApplicationStatus.UPDATING ? (
              <Spinner
                data-hook={AmountInputDataHooks.TextLoader}
                type={SpinnerTypes.slim}
                diameter={16}
              />
            ) : null
          }
          error={!!giftCardError}
          onChange={
            customRedeemAmountIsEnabled
              ? (e) => validateAndUpdateAmount(e.target.value)
              : undefined
          }
        />
        <StatesButton
          className={classes.applyStatesButton}
          data-hook={AmountInputDataHooks.ApplyButton}
          idleContent={
            customRedeemAmountIsEnabled
              ? statesButtonIdleLabel()
              : localeKeys.checkoutSlot.redeemStoreCredit.cta()
          }
          disabled={isButtonDisabled}
          state={
            status === ApplicationStatus.UPDATING
              ? StatesButtonStates.IN_PROGRESS
              : StatesButtonStates.IDLE
          }
          onClick={() => {
            updateCheckout(
              customRedeemAmountIsEnabled ? customRedeemAmount : undefined,
            );
            reportClickBiEvent({
              buttonName: BIClickEvents.APPLY,
              errorName: getErrorSectionText(),
            });
          }}
          inProgressContent={
            <ThreeDotsLoader
              data-hook={AmountInputDataHooks.ThreeDotsLoaderIcon}
              className={classes.threeDotButton}
            />
          }
          aria-describedby={`${AmountInputDataHooks.TextInput} ${ErrorSectionDataHooks.ErrorText}`}
          ref={statesButtonRef}
        />
      </div>
      <ErrorSection />
    </div>
  );
};
